import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

import type { WalletLoaded } from '@sorare/wallet-shared';

import { useMessagingContext } from '#src/contexts/wallet/index.ts';

type WalletLoadedCallback = () => void;

interface WalletLoadedContextValue {
  subscribe: (callback: WalletLoadedCallback) => () => void;
}

const WalletLoadedContext = createContext<WalletLoadedContextValue | null>(
  null
);

/** No-op subscribe for environments without `<WalletLoadedProvider>` (mocks, storybook). */
const noopSubscribe = () => () => {};

export const useOnWalletLoaded = () => {
  const ctx = useContext(WalletLoadedContext);
  return ctx?.subscribe ?? noopSubscribe;
};

/**
 * Owns the single `registerHandler<WalletLoaded>('walletLoaded', …)` registration.
 * Multiple consumers can subscribe via `useOnWalletLoaded()` and each will be
 * invoked when the wallet iframe sends a `walletLoaded` message. This avoids
 * the last-write-wins handler collision that occurred when both
 * `useCreateAndLoadWallet` and `useOAuthEthWallet` registered their own
 * `walletLoaded` handler.
 */
export const WalletLoadedProvider = ({ children }: { children: ReactNode }) => {
  const { registerHandler } = useMessagingContext();
  const subscribersRef = useRef<Set<WalletLoadedCallback>>(new Set());

  useEffect(
    () =>
      registerHandler<WalletLoaded>('walletLoaded', async () => {
        subscribersRef.current.forEach(cb => cb());
        return {};
      }),
    [registerHandler]
  );

  const subscribe = useCallback((callback: WalletLoadedCallback) => {
    subscribersRef.current.add(callback);
    return () => {
      subscribersRef.current.delete(callback);
    };
  }, []);

  return (
    <WalletLoadedContext value={{ subscribe }}>{children}</WalletLoadedContext>
  );
};
